import {
  addYouthUsage,
  getYouthUsage,
  checkYouthAccess,
  unlockYouth,
  isYouthUnlocked,
  getYouthConfig,
} from '@/api/index';

class YouthModeManager {
  constructor() {
    this._intervalId = null;
    this._listeners = [];
    // 适龄内容池（与后端 YouthMode::YOUTH_WHITELIST、Home.vue youthWhitelist 保持一致）
    this.whitelist = [507, 511, 510];
    this._readyPromise = null;
    this._state = {
      enabled: false,
      blocked: false,
      blockReason: '',
      usageMinutes: 0,
      dailyTimeLimit: 60,
      curfewActive: false,
      showBlockOverlay: false,
      showPasswordModal: false,
      unlocked: false,
      // 《移动互联网未成年人模式建设指南》：连续使用超过30分钟应发出休息提醒
      continuousMinutes: 0,
      showRestReminder: false,
      showContentBlocked: false,
    };
  }

  get state() {
    return this._state;
  }

  _notify() {
    this._listeners.forEach(fn => fn(this._state));
  }

  onStateChange(fn) {
    this._listeners.push(fn);
    return () => {
      this._listeners = this._listeners.filter(f => f !== fn);
    };
  }

  async init() {
    const token = localStorage.getItem('userToken');
    if (!token) return;

    this._readyPromise = (async () => {
      try {
        const configRes = await getYouthConfig();
        if (configRes.data && configRes.data.code === 1) {
          this._state.enabled = configRes.data.data.enabled;
          this._state.dailyTimeLimit = configRes.data.data.daily_time_limit;
        }
      } catch (e) {
        console.warn('[YouthMode] init failed', e);
      }
    })();

    await this._readyPromise;
    this._notify();
    this._startTimer();
  }

  // 供页面在过滤内容前等待配置就绪
  async ensureReady() {
    if (!localStorage.getItem('userToken')) return;
    if (!this._readyPromise) {
      this.init();
      return;
    }
    try {
      await this._readyPromise;
    } catch (e) {
      // ignore
    }
  }

  // 未成年人保护：青少年模式开启时，仅允许访问适龄内容池
  isComicAllowed(comicId) {
    if (!this._state.enabled) return true;
    return this.whitelist.includes(Number(comicId));
  }

  // 列表过滤：青少年模式开启时仅保留内容池作品
  filterForYouth(list) {
    if (!this._state.enabled || !Array.isArray(list)) return list;
    return list.filter(item => this.whitelist.includes(Number(item.id)));
  }

  // 内容被内容池拦截时的全局提示
  notifyContentBlocked() {
    this._state.showContentBlocked = true;
    this._notify();
  }

  dismissContentBlocked() {
    this._state.showContentBlocked = false;
    this._notify();
  }

  destroy() {
    this._stopTimer();
  }

  _startTimer() {
    this._stopTimer();
    this._intervalId = setInterval(() => this._tick(), 60000);
    this._tick();
  }

  _stopTimer() {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }

  async _tick() {
    const token = localStorage.getItem('userToken');
    if (!token || !this._state.enabled) return;

    try {
      // 先检查解锁状态
      const unlockRes = await isYouthUnlocked();
      if (unlockRes.data && unlockRes.data.code === 1) {
        this._state.unlocked = unlockRes.data.data.unlocked;
      }

      // 解锁期间不累计使用时长
      if (!this._state.unlocked) {
        await addYouthUsage(1);
      }

      // 连续使用计时：解锁/被拦截时不累计，满30分钟触发休息提醒
      if (this._state.unlocked || this._state.blocked) {
        this._state.continuousMinutes = 0;
      } else {
        this._state.continuousMinutes += 1;
        if (this._state.continuousMinutes >= 30) {
          this._state.showRestReminder = true;
        }
      }

      const usageRes = await getYouthUsage();
      if (usageRes.data && usageRes.data.code === 1) {
        const usage = usageRes.data.data;
        this._state.usageMinutes = usage.usage_minutes;
        this._state.dailyTimeLimit = usage.daily_time_limit;
      }

      const accessRes = await checkYouthAccess();
      if (accessRes.data && accessRes.data.code === 1) {
        const access = accessRes.data.data;
        this._state.blocked = access.blocked;
        this._state.blockReason = access.reason;
        this._state.curfewActive = access.curfew_active;
        this._state.showBlockOverlay = access.blocked;
      }

      if (!this._state.blocked) {
        this._state.showBlockOverlay = false;
      }
    } catch (e) {
      console.warn('[YouthMode] tick error', e);
    }

    this._notify();
  }

  async unlockWithPassword(password) {
    try {
      const res = await unlockYouth(password);
      if (res.data && res.data.code === 1) {
        this._state.showBlockOverlay = false;
        this._state.showPasswordModal = false;
        this._state.unlocked = true;
        this._state.blocked = false;
        this._notify();
        return { success: true };
      }
      return { success: false, message: res.data.msg || '解锁失败' };
    } catch (e) {
      return { success: false, message: '网络错误' };
    }
  }

  showPasswordModal() {
    this._state.showPasswordModal = true;
    this._notify();
  }

  hidePasswordModal() {
    this._state.showPasswordModal = false;
    this._notify();
  }

  // 确认休息提醒后重置连续使用计时
  acknowledgeRest() {
    this._state.showRestReminder = false;
    this._state.continuousMinutes = 0;
    this._notify();
  }

  async checkBeforePurchase() {
    const token = localStorage.getItem('userToken');
    if (!token) return { allowed: true };

    try {
      const configRes = await getYouthConfig();
      if (configRes.data && configRes.data.code === 1) {
        if (configRes.data.data.enabled) {
          return { allowed: false, message: '青少年模式已开启，暂不支持购买操作' };
        }
      }
    } catch (e) {
      // ignore
    }
    return { allowed: true };
  }
}

export default new YouthModeManager();